import React, { useMemo, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Modal, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import { Icon } from '../../../../components/Icon/Icon';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import AccessServiceTypePicker from '../../../../components/AccessServiceTypePicker';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import useStaff from '../../../../hooks/useStaff';
import useAccessServiceTypes from '../../../../hooks/useAccessServiceTypes';
import api from '../../../../utils/api';


export default function GuardNewAccessAuthorizationModal({ visible, communityId, onRequestClose, onCreated }: any) {
  const insets = useSafeAreaInsets();
  const { staff } = useStaff();
  const community = staff?.find((c: any) => c.uuid === communityId);
  const { catalogEnabled, descriptionMode, types, isLoading: typesLoading } = useAccessServiceTypes(communityId, community?.settings);

  const { control, handleSubmit, watch, setValue, reset } = useForm<any>({
    defaultValues: {
      unit_uuid: '',
      service_type_uuid: '',
      description: '',
      access_type: 'vehicular',
      provider_name: '',
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: '',
      start_time: '',
      end_time: '',
    },
  });

  React.useEffect(() => {
    if (visible) reset();
  }, [visible]);

  const [unitSearch, setUnitSearch] = useState('');

  const unitsQuery = useQuery<any>({
    queryKey: ['guard.units.lookup', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units`).then(r => r.data),
    enabled: !!communityId && !!visible,
  });

  const units: any[] = useMemo(() => {
    const all = unitsQuery.data?.data ?? unitsQuery.data ?? [];
    if (!unitSearch.trim()) return all;
    const q = unitSearch.toLowerCase();
    return all.filter((u: any) => (u.name ?? '').toLowerCase().includes(q));
  }, [unitsQuery.data, unitSearch]);

  const unitUuid = watch('unit_uuid');
  const accessType = watch('access_type');
  const serviceTypeUuid = watch('service_type_uuid');

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onCreated,
    onError: () => {},
    successToastMessage: 'Autorización creada',
    endpoint: `/communities/${communityId}/access-authorizations`,
  });

  function onSave(data: any) {
    submitForm({
      unit_uuid: data.unit_uuid,
      ...(catalogEnabled && { service_type_uuid: data.service_type_uuid }),
      description: data.description?.trim() || null,
      access_type: data.access_type,
      provider_name: data.provider_name?.trim() || null,
      start_date: data.start_date,
      end_date: data.end_date || null,
      start_time: data.start_time || null,
      end_time: data.end_time || null,
      auto_approve: true,
    });
  }

  return (
    <Modal visible={!!visible} onRequestClose={onRequestClose} animationType='slide' statusBarTranslucent>
      <View style={tw`flex-1 bg-white pt-[${insets.top}px]`}>
        <View style={tw`row justify-between items-center px-5 py-3 border-b border-gray-200`}>
          <Text style={tw`text-primary text-lg font-600`}>Nueva autorización</Text>
          <TouchableOpacity onPress={onRequestClose}>
            <Icon name='close' size={26} color={tw.color('red')} />
          </TouchableOpacity>
        </View>

        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={tw`flex-1`}>
          <ScrollView contentContainerStyle={tw`px-5 py-4 pb-10`}>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Se dirige a</Text>
            <View style={tw`flex-row items-center bg-gray-50 border border-gray-200 rounded-full px-4 py-2 mb-2`}>
              <TextInput
                value={unitSearch}
                onChangeText={setUnitSearch}
                placeholder='Buscar vivienda'
                placeholderTextColor='#9ca3af'
                style={tw`flex-1 text-sm text-gray-900`}
              />
            </View>
            <View style={tw`max-h-40 border border-gray-200 rounded-lg mb-5`}>
              {unitsQuery.isLoading && <View style={tw`p-4 items-center`}><ActivityIndicator /></View>}
              <ScrollView>
                {units.slice(0, 30).map((u: any) => (
                  <TouchableOpacity
                    key={u.uuid}
                    onPress={() => setValue('unit_uuid', u.uuid)}
                    style={tw.style(`px-3 py-2 border-b border-gray-100 flex-row justify-between`, unitUuid === u.uuid && `bg-primary/5`)}
                  >
                    <Text style={tw.style(`text-gray-700`, unitUuid === u.uuid && `text-primary font-600`)}>{u.name}</Text>
                    {unitUuid === u.uuid && <Text style={tw`text-primary`}>✓</Text>}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <Text style={tw`text-sm text-gray-600 mb-1`}>Quién autoriza (nombre del proveedor)</Text>
            <Input control={control} name='provider_name' placeholder='Nombre y apellido' />

            {catalogEnabled && (
              <>
                <Text style={tw`text-sm text-gray-600 mb-2`}>Tipo de servicio</Text>
                <AccessServiceTypePicker
                  types={types}
                  value={serviceTypeUuid}
                  onChange={(uuid) => setValue('service_type_uuid', uuid)}
                  isLoading={typesLoading}
                />
              </>
            )}

            {descriptionMode !== 'off' && (
              <>
                <Text style={tw`text-sm text-gray-600 mb-1`}>Descripción del servicio{descriptionMode === 'optional' ? ' (opc.)' : ''}</Text>
                <Input control={control} name='description' rules={{ required: descriptionMode === 'required' }} multiline numberOfLines={3} />
              </>
            )}

            <Text style={tw`text-sm text-gray-600 mb-2`}>Tipo de acceso</Text>
            <View style={tw`flex-row gap-2 mb-5`}>
              {[
                { key: 'vehicular', label: 'Vehicular' },
                { key: 'pedestrian', label: 'Peatonal' },
              ].map(opt => (
                <TouchableOpacity
                  key={opt.key}
                  onPress={() => setValue('access_type', opt.key)}
                  style={tw.style(`px-4 py-2 rounded-full border`, accessType === opt.key ? 'border-transparent bg-primary' : 'border-gray-200 bg-white')}
                >
                  <Text style={tw.style(`text-xs font-500`, accessType === opt.key ? 'text-white' : 'text-gray-700')}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={tw`flex-row gap-3`}>
              <View style={tw`flex-1`}>
                <Text style={tw`text-sm text-gray-600 mb-1`}>Desde</Text>
                <Input control={control} name='start_date' rules={{ required: true }} placeholder='YYYY-MM-DD' />
              </View>
              <View style={tw`flex-1`}>
                <Text style={tw`text-sm text-gray-600 mb-1`}>Hasta (opc.)</Text>
                <Input control={control} name='end_date' placeholder='YYYY-MM-DD' />
              </View>
            </View>

            <View style={tw`flex-row gap-3`}>
              <View style={tw`flex-1`}>
                <Text style={tw`text-sm text-gray-600 mb-1`}>Hora inicio (opc.)</Text>
                <Input control={control} name='start_time' placeholder='HH:mm' />
              </View>
              <View style={tw`flex-1`}>
                <Text style={tw`text-sm text-gray-600 mb-1`}>Hora fin (opc.)</Text>
                <Input control={control} name='end_time' placeholder='HH:mm' />
              </View>
            </View>

            <IconButton
              disabled={isSubmitting || !unitUuid || (catalogEnabled && !serviceTypeUuid)}
              title='Crear autorización'
              style={tw`mt-4`}
              onPress={handleSubmit(onSave)}
            />
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}
